import { UserProfileModel } from './user-profile.model';

/**
 * Modèle front des acteurs achat (PRED-3139) — miroir typé du contrat back
 * `back/src/modules/tenant/settings/purchase-actor/`.
 */

/** Miroir de `PurchaseActorScopeEnum` (back). */
export enum PurchaseActorScopeEnum {
  TENANT = 'tenant',
  ESTABLISHMENT = 'establishment',
}

/**
 * Miroir de `PurchaseActorRoleEnum` (back) — les 6 rôles. Les valeurs SONT les noms de
 * champs techniques du document (`buisnessReferents`, `referentsN1`...) : ne jamais les
 * afficher tels quels côté UI, utiliser `PURCHASE_ACTOR_ROLE_LABELS`.
 */
export enum PurchaseActorRoleEnum {
  BUISNESS_REFERENTS = 'buisnessReferents',
  REFERENTS_N1 = 'referentsN1',
  REFERENTS_N2 = 'referentsN2',
  APPROVER = 'approver',
  SUPERVISIONS = 'supervisions',
}

export const PURCHASE_ACTOR_ALL_ROLES: PurchaseActorRoleEnum[] = Object.values(PurchaseActorRoleEnum);

/** Ordre d'AFFICHAGE des rôles dans les écrans (≠ ordre technique de l'enum). */
export const PURCHASE_ACTOR_ROLE_DISPLAY_ORDER: PurchaseActorRoleEnum[] = [
  PurchaseActorRoleEnum.BUISNESS_REFERENTS,
  PurchaseActorRoleEnum.REFERENTS_N1,
  PurchaseActorRoleEnum.REFERENTS_N2,
  PurchaseActorRoleEnum.SUPERVISIONS,
  PurchaseActorRoleEnum.APPROVER,
];

/** approver est mono-user, tous les autres rôles sont multi-users. */
export const PURCHASE_ACTOR_MULTI_ROLES: PurchaseActorRoleEnum[] = PURCHASE_ACTOR_ALL_ROLES.filter(
  role => role !== PurchaseActorRoleEnum.APPROVER,
);

/** Niveau de famille le plus profond paramétrable — miroir de `PURCHASE_ACTOR_MAX_CATEGORY_LEVEL` (back). */
export const PURCHASE_ACTOR_MAX_CATEGORY_LEVEL = 2;

/**
 * Libellés utilisateur (FR) des rôles — miroir de `PURCHASE_ACTOR_ROLE_LABELS` (back
 * `purchase-actor.constant.ts`). À utiliser partout où un rôle est affiché au client.
 */
export const PURCHASE_ACTOR_ROLE_LABELS: Record<PurchaseActorRoleEnum, string> = {
  [PurchaseActorRoleEnum.BUISNESS_REFERENTS]: 'Référents engagement',
  [PurchaseActorRoleEnum.REFERENTS_N1]: 'Validateurs N+1',
  [PurchaseActorRoleEnum.REFERENTS_N2]: 'Validateurs N+2',
  [PurchaseActorRoleEnum.APPROVER]: 'Approbateur de facture sans commande',
  [PurchaseActorRoleEnum.SUPERVISIONS]: 'Supervision métier',
};

/**
 * Association rôle → flag de filtre `UserProfileService.findAll` (mêmes flags que l'ancien
 * écran validators). Centralisé ici (DRY) : consommé par `purchase-actors-panel` (autocomplete
 * par rôle) ET par `purchase-actor-contact-replacement-modal` (union des flags des rôles tenus
 * par le contact sortant). Cf. `UserProfileFilteringService` (back) : chaque flag pousse une
 * condition `contactType.label` dans `andMatch`, combinées avec `$and` — passer plusieurs flags
 * simultanément restreint donc déjà aux users portant TOUS les contactTypes demandés (ET, pas OU).
 */
export const PURCHASE_ACTOR_ROLE_FILTER_FLAG: Record<PurchaseActorRoleEnum, string> = {
  [PurchaseActorRoleEnum.BUISNESS_REFERENTS]: 'isReferentEngagement',
  [PurchaseActorRoleEnum.REFERENTS_N1]: 'isValidator',
  [PurchaseActorRoleEnum.REFERENTS_N2]: 'isValidator',
  [PurchaseActorRoleEnum.APPROVER]: 'isApprover',
  [PurchaseActorRoleEnum.SUPERVISIONS]: 'isSupervision',
};

/** Contact éligible retourné par `UserProfileService.findAll` — miroir de `DefaultSupervisionAssignableContact`. */
export type PurchaseActorAssignableContact = UserProfileModel & { fullName?: string };

/** Miroir de `PurchaseActorSourceEnum` (back `purchase-actor.interface.ts`) — 6 niveaux de cascade, du plus prioritaire au moins prioritaire. */
export enum PurchaseActorSourceEnum {
  ESTABLISHMENT_CATEGORY = 'establishmentCategory',
  ESTABLISHMENT_ANCESTOR = 'establishmentAncestor',
  ESTABLISHMENT_DEFAULT = 'establishmentDefault',
  TENANT_CATEGORY = 'tenantCategory',
  TENANT_ANCESTOR = 'tenantAncestor',
  TENANT_DEFAULT = 'tenantDefault',
}

/** Contact (user-profile) référencé dans un rôle — miroir de `UserProfileRelation` (subset exposé). */
export interface PurchaseActorContact {
  _id: string;
  firstName: string;
  lastName: string;
  /** Absent des snapshots persistés ({_id, firstName, lastName}) — recalculé côté front pour
   * l'affichage (les composants d'autocomplete rendent `displayProperty="fullName"`). */
  fullName?: string;
}

/** Nœud (catégorie/établissement) ayant fourni une valeur résolue — miroir du `node` back. */
export interface PurchaseActorNodeRef {
  _id: string;
  label: string;
}

/** Établissement référencé dans un payload d'affectation — miroir de `EstablishmentRelation` (subset exposé). */
export interface PurchaseActorEstablishmentRef {
  _id: string;
  label: string;
  technicalIdentifier?: string;
}

/** Résolution d'un rôle unique — miroir de `ResolvedPurchaseActorRole` (back). */
export interface ResolvedPurchaseActorRole {
  users: PurchaseActorContact[];
  /** `null` = non couvert par la cascade. */
  source: PurchaseActorSourceEnum | null;
  node?: PurchaseActorNodeRef;
}

/**
 * Résolution complète (6 rôles + autorizationOrder) — miroir de `ResolvedPurchaseActors` (back).
 * `autorizationOrder` (invariant PRED-3207 aménagé) : premier SIGNAL de la cascade — approbateur
 * stocké à un niveau = `true`, sinon flag explicite (opt-out/réactivation) — court-circuité à
 * `{value: false, source: null}` si aucun approbateur effectif n'est résolu.
 */
export type ResolvedPurchaseActors = {
  [role in PurchaseActorRoleEnum]: ResolvedPurchaseActorRole;
} & {
  autorizationOrder: { value: boolean; source: PurchaseActorSourceEnum | null };
};

/**
 * Doc explicite (non résolu) d'un nœud — miroir de `PurchaseActorEntity` (back
 * `purchase-actor.schema.ts`). Rôle ABSENT (undefined) = hérite via la cascade.
 */
export interface PurchaseActorExplicit {
  _id: string;
  scope: PurchaseActorScopeEnum;
  establishment?: PurchaseActorEstablishmentRef;
  /** `undefined`/absent = « défaut » du scope (ex-defaultValidators / config client). */
  category?: PurchaseActorNodeRef;
  buisnessReferents?: PurchaseActorContact[];
  referentsN1?: PurchaseActorContact[];
  referentsN2?: PurchaseActorContact[];
  /** Mono-utilisateur (contrairement aux 5 autres rôles). */
  approver?: PurchaseActorContact;
  purchasers?: PurchaseActorContact[];
  supervisions?: PurchaseActorContact[];
  /** Tri-état aménagé PRED-3207 : absent = hérite du signal cascade ; `false` = opt-out
   * (approbateur hérité) ; `true` = réactivation sous un opt-out. Jamais porté par un nœud
   * ayant son propre `approver` (purgé à l'écriture). */
  autorizationOrder?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** `GET /purchase-actor/resolve` response. */
export interface PurchaseActorResolveResponse {
  resolved: ResolvedPurchaseActors;
  /** Résolution SANS la config propre du nœud : ce que chaque rôle hériterait si sa surcharge
   * était retirée — base d'affichage de « Revenir à l'héritage » et du diff au save. */
  inherited?: ResolvedPurchaseActors;
  explicit: PurchaseActorExplicit | null;
}

/** `GET /purchase-actor/configured-nodes` response. */
export interface PurchaseActorConfiguredNodesResponse {
  categoryIds: string[];
  hasDefault: boolean;
}

/**
 * `POST /purchase-actor/assign` payload — miroir de `PurchaseActorAssignValidation` (back).
 * Sémantique PLEIN REMPLACEMENT : décrit l'état complet voulu pour CE nœud (un rôle
 * absent/tableau vide est traité comme « à retirer »).
 */
export interface PurchaseActorAssignPayload {
  scope: PurchaseActorScopeEnum;
  /** Requis si `scope === ESTABLISHMENT`. */
  establishment?: PurchaseActorEstablishmentRef;
  /** Absent => « défaut » du scope (racine sans catégorie). */
  categoryId?: string;
  buisnessReferents?: string[];
  referentsN1?: string[];
  referentsN2?: string[];
  /** Mono-utilisateur. */
  approver?: string;
  purchasers?: string[];
  supervisions?: string[];
  /** Tri-état aménagé PRED-3207 : absent => hérite du signal ; `false` = opt-out / `true` =
   * réactivation quand l'approbateur est HÉRITÉ. Ignoré (et purgé) par le back si `approver`
   * est défini dans ce même payload. */
  autorizationOrder?: boolean;
  /**
   * Si `true` : efface, pour CE scope, les rôles édités par cet appel sur les sous-familles du
   * nœud — le sous-arbre de `categoryId` si fournie, TOUTES les familles du scope si absente
   * (nœud « défaut », racine de la cascade).
   */
  clearChildrenOverrides?: boolean;
}

/** `POST /purchase-actor/unassign` payload — miroir de `PurchaseActorUnassignValidation` (back). */
export interface PurchaseActorUnassignPayload {
  scope: PurchaseActorScopeEnum;
  /** Requis si `scope === ESTABLISHMENT`. */
  establishmentId?: string;
  categoryId?: string;
}

/** `POST /purchase-actor/replaceContact` payload — miroir de `PurchaseActorReplaceContactValidation` (back). */
export interface PurchaseActorReplaceContactPayload {
  oldContactId: string;
  /** Absent => retrait pur du contact des rôles où il figure (pas de remplaçant). */
  newContact?: PurchaseActorContact;
  /** Limite le remplacement aux docs `scope: establishment` de cet établissement. */
  establishmentId?: string;
}

/**
 * Miroir de `PurchaseActorCheckDependenciesResult` (back `purchase-actor.service.ts`) — étend
 * `ReturnCheckDependencies` avec `rolesHeld`, l'union structurée (enum) des rôles tenus par le
 * contact sortant sur tous les docs impactés. Optionnel : réponses plus anciennes / autres
 * endpoints partageant ce contrat générique peuvent ne pas le porter (cf. fallback dans la modale).
 */
export interface PurchaseActorDependencies {
  data: { name: string; dependencies: string[] }[];
  hasDependencies: boolean;
  canChangeDependencies: boolean;
  canArchiveWithDependencies: boolean;
  rolesHeld?: PurchaseActorRoleEnum[];
}
